import { useCallback, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Center,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconHelpCircle, IconSettings } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client';
import type { Child } from '../api/gen';
import { ChildAvatar } from '../components/ChildAvatar';
import { Keypad } from '../components/Keypad';
import { useSession } from '../lib/session';
import { accent } from '../theme';

// The screen the app boots to, every afternoon: who is learning today.
//
// The roster comes from the device's pairing, so this needs no network and no
// parent. Tapping a name asks for four digits; the server checks them, and five
// wrong tries lock the profile for a minute — which is what the message says,
// because "falsch" over and over is what makes a child give up.
export function ProfileSelectPage() {
  const { pairing, signInChild } = useSession();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Child | null>(null);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const children = pairing?.children ?? [];

  const submit = useCallback(
    async (value: string) => {
      if (!selected || busy) return;
      setBusy(true);
      try {
        await signInChild(selected.id, value);
        setSelected(null);
        setPin('');
      } catch (err) {
        setPin('');
        const message =
          err instanceof ApiError && err.status === 429
            ? 'Zu viele Versuche. Warte eine Minute und probiere es dann noch einmal.'
            : err instanceof ApiError && err.status === 401
              ? 'Das war nicht die richtige PIN.'
              : (err as Error).message;
        notifications.show({ color: 'red', message });
      } finally {
        setBusy(false);
      }
    },
    [selected, signInChild, busy],
  );

  if (selected) {
    const a = accent(selected.mode);
    return (
      <Center mih="100vh" p="lg" style={{ background: a.soft }}>
        <Stack align="center" w={320} maw="100%" gap="lg">
          <ChildAvatar name={selected.name} mode={selected.mode} size={72} />
          <Box ta="center">
            <Title order={3}>Hallo {selected.name}</Title>
            <Text c="dimmed" size="sm">
              Gib deine PIN ein
            </Text>
          </Box>

          <Keypad pin={pin} onChange={setPin} onComplete={submit} color={a.color} disabled={busy} />

          <Button
            variant="subtle"
            color="gray"
            onClick={() => {
              setSelected(null);
              setPin('');
            }}
          >
            Zurück
          </Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Center mih="100vh" p="md">
      <Stack w={440} maw="100%">
        <Group justify="space-between">
          <Title order={2}>Wer lernt heute?</Title>
          <Group gap="xs">
            <ActionIcon
              size="lg"
              aria-label="Hilfe"
              onClick={() => navigate('/hilfe')}
            >
              <IconHelpCircle size={20} />
            </ActionIcon>
            <ActionIcon
              size="lg"
              aria-label="Eltern-Modus"
              onClick={() => navigate('/eltern')}
            >
              <IconSettings size={20} />
            </ActionIcon>
          </Group>
        </Group>

        {children.length === 0 ? (
          <Card withBorder padding="lg">
            <Stack gap="sm" align="start">
              <Text fw={600}>Es gibt noch kein Kinderprofil.</Text>
              <Text c="dimmed" size="sm">
                Öffne den Eltern-Modus über das Zahnrad und lege eines an.
              </Text>
            </Stack>
          </Card>
        ) : (
          <SimpleGrid cols={2}>
            {children.map((c) => (
              <Card
                key={c.id}
                component="button"
                padding="lg"
                style={{ cursor: 'pointer', textAlign: 'center', font: 'inherit' }}
                onClick={() => setSelected(c)}
              >
                <Stack align="center" gap="xs">
                  <ChildAvatar name={c.name} mode={c.mode} />
                  <Text fw={600}>{c.name}</Text>
                  <Text size="xs" c="dimmed">
                    {accent(c.mode).label}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </Center>
  );
}
